'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';

export type WorkspaceRole = 'admin' | 'manager' | 'member' | 'viewer';

interface PermissionContextValue {
  workspaceRole: WorkspaceRole | null;
  loadingRole: boolean;
  canEditProjects: boolean;
  canEditSprints: boolean;
  canEditMembers: boolean;
  canEditTask: (assigneeId?: string | null) => boolean;
  isReadOnly: boolean;
  loadRoleForWorkspace: (workspaceId: string) => Promise<void>;
}

const PermissionContext = createContext<PermissionContextValue>({
  workspaceRole: null,
  loadingRole: false,
  canEditProjects: false,
  canEditSprints: false,
  canEditMembers: false,
  canEditTask: () => false,
  isReadOnly: true,
  loadRoleForWorkspace: async () => {},
});

export const usePermissions = () => useContext(PermissionContext);

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [workspaceRole, setWorkspaceRole] = useState<WorkspaceRole | null>(null);
  const [loadingRole, setLoadingRole] = useState(false);

  const loadRoleForWorkspace = useCallback(async (workspaceId: string) => {
    if (!user || !workspaceId) return;
    setLoadingRole(true);
    try {
      const res = await fetch(`/api/members?workspaceId=${workspaceId}`);
      if (res.ok) {
        const members = await res.json();
        // members shape: { id: membership_id, userId, role, ... }
        const member = members.find((m: any) => m.userId === user.id);
        if (member?.role) {
          setWorkspaceRole(member.role as WorkspaceRole);
        } else {
          // Check if user is workspace owner — fetch workspaces and check
          const wsRes = await fetch('/api/workspaces');
          if (wsRes.ok) {
            const workspaces = await wsRes.json();
            const ws = workspaces.find((w: any) => w.id === workspaceId);
            setWorkspaceRole(ws?.ownerId === user.id ? 'admin' : 'viewer');
          } else {
            setWorkspaceRole('viewer');
          }
        }
      } else {
        setWorkspaceRole('viewer');
      }
    } catch {
      setWorkspaceRole('viewer');
    } finally {
      setLoadingRole(false);
    }
  }, [user]);

  const canEditProjects = workspaceRole === 'admin' || workspaceRole === 'manager';
  const canEditSprints = workspaceRole === 'admin' || workspaceRole === 'manager';
  const canEditMembers = workspaceRole === 'admin';
  const isReadOnly = workspaceRole === 'viewer';

  const canEditTask = useCallback((_assigneeId?: string | null): boolean => {
    if (!workspaceRole) return false;
    if (workspaceRole === 'viewer') return false;
    // admin, manager, and member (any workspace participant) can update tasks
    return true;
  }, [workspaceRole]);

  return (
    <PermissionContext.Provider value={{
      workspaceRole,
      loadingRole,
      canEditProjects,
      canEditSprints,
      canEditMembers,
      canEditTask,
      isReadOnly,
      loadRoleForWorkspace,
    }}>
      {children}
    </PermissionContext.Provider>
  );
}
